
import { computed, defineComponent, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Modal from '@/common/components/UI/Modal/index.vue'

// module components
import ChangesHistoryFilters from '@/modules/ChangesHistoryModule/components/ChangesHistoryFilters/index.vue'

// constants
import { ChangesHistoryColumns } from '@/core/constants/columns/ChangesHistory.columns'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import {
    ChangesHistoryGetListParams,
    ChangesHistoryGetListResponseT,
    ChangesHistoryItemT,
} from '@/core/types/changes_history.types'

// realisations
import { changes_history } from '@/core/realisations/changes_history.realisation'
import { orders } from '@/core/realisations/orders.realisation'

const IGNORE_KEYS = ['updated_at', 'created_at', 'status_id', 'value']
const WITHOUT_KEYS = ['status_name']

export default defineComponent({
    components: {
        PageTitle,
        FailedFetch,
        TableScrollWrapper,
        TheTable,
        TheButton,
        ChangesHistoryFilters,
        Modal,
    },
    setup() {
        const state = reactive({
            current_item: null as ChangesHistoryItemT | null,
        })

        const data_state = reactive({
            changes_history: new DataState<ChangesHistoryItemT>({}),
        })

        const filters_state = reactive({
            clientId: null as number | null,
            typeId: null as number | null,
            actionId: null as number | null,
        })

        const setAdditionalTableInfo = async () => {
            if (filters_state.typeId === 2 && filters_state.clientId) {
                const response_invoice = await orders.getInvoiceByInvoiceNumber(
                    filters_state.clientId
                )

                response_invoice.data.additional_services_values.forEach(async (service) => {
                    const params_invoices = {
                        limit: 50,
                        actionId: filters_state.actionId,
                        clientId: service.id,
                        typeId: 4,
                    }

                    const response = await changes_history.getList(1, params_invoices)

                    data_state.changes_history.list = [
                        ...data_state.changes_history.list,
                        ...response.data,
                    ]
                })
            }
        }

        const setTableInfo = (response: ChangesHistoryGetListResponseT) => {
            data_state.changes_history.list = response.data
            data_state.changes_history.pagination.total_pages = response.meta.last_page

            data_state.changes_history.is_failed = false
            data_state.changes_history.failure.error_state.is_cancel = false

            // если в фильтре выбраны LTL-накладные
            setAdditionalTableInfo()
        }

        const loadChangesHistory = async (page = 1) => {
            data_state.changes_history.pagination.current_page = page

            try {
                data_state.changes_history.is_loading = true

                const params = {
                    limit: 50,
                    ...filters_state,
                }

                const response = await changes_history.getList(page, params)

                setTableInfo(response)
            } catch (error) {
                data_state.changes_history.failure.catchError(error)

                if (!data_state.changes_history.failure.error_state.is_cancel) {
                    data_state.changes_history.is_failed = true
                }
            } finally {
                if (!data_state.changes_history.failure.error_state.is_cancel) {
                    data_state.changes_history.is_loading = false
                }
            }
        }

        const onSubmitFilters = (filters: ChangesHistoryGetListParams) => {
            filters_state.clientId = filters.clientId
            filters_state.typeId = filters.typeId
            filters_state.actionId = filters.actionId

            loadChangesHistory(1)
        }

        const onResetFilters = () => {
            filters_state.clientId = null
            filters_state.typeId = null
            filters_state.actionId = null

            loadChangesHistory(1)
        }

        const cutName = (name: string, max_length: number): string => {
            if (!name) return '-'

            if (name && name.length > max_length) {
                let name_parts = name.split('')
                name_parts = name_parts.splice(0, max_length - 1)
                name_parts.push('...')
                return name_parts.join('')
            }

            return name
        }

        const getDifferencesDirectionInfo = (
            result: string,
            item: ChangesHistoryItemT,
            type: string
        ) => {
            // проверка на разницу в RECEIVER из new_value и old_value
            let receiver_old = item.old_value.direction_info.receiver,
                receiver_new = item.new_value.direction_info.receiver

            for (const [key, value] of Object.entries(receiver_old)) {
                if (value !== receiver_new[key] && !IGNORE_KEYS.includes(key)) {
                    result += `${result ? ',' : ''} ${key} (receiver): ${
                        type === 'old' ? value : receiver_new[key]
                    }`
                }
            }

            // проверка на разницу в SENDER из new_value и old_value
            let sender_old = item.old_value.direction_info.sender,
                sender_new = item.new_value.direction_info.sender

            for (const [key, value] of Object.entries(sender_old)) {
                if (value !== sender_new[key] && !IGNORE_KEYS.includes(key)) {
                    result += `${result ? ',' : ''} ${key} (sender): ${
                        type === 'old' ? value : sender_new[key]
                    }`
                }
            }

            return result
        }

        const getResult = (result: string, component_type: string) => {
            if (result) {
                return component_type === 'table' ? cutName(result, 30) : result
            } else {
                return '-'
            }
        }

        const getValueObj = (type: string, item: ChangesHistoryItemT, is_main: boolean) => {
            if (type === 'old') {
                return is_main ? item.old_value : item.new_value
            } else {
                return is_main ? item.new_value : item.old_value
            }
        }

        const getDifferences = (
            item: ChangesHistoryItemT,
            type: string,
            component_type: string
        ) => {
            let result = ''

            if (item.actionId !== 2 || !item.old_value || !item.new_value) return '-'

            let main = getValueObj(type, item, true),
                additional = getValueObj(type, item, false)

            for (const [key, value] of Object.entries(main)) {
                if (key === 'direction_info') {
                    result = getDifferencesDirectionInfo(result, item, type)
                } else if (value !== additional[key] && !IGNORE_KEYS.includes(key)) {
                    if (WITHOUT_KEYS.includes(key)) {
                        result += `${result ? ',' : ''} ${value}`
                    } else {
                        result += `${result ? ',' : ''} ${key}: ${value}`
                    }
                }
            }

            return getResult(result, component_type)
        }

        const getNewValueArr = (item: ChangesHistoryItemT) => {
            let result: string[] = []

            let new_values = Object.values(item.new_value)
            let keys = Object.keys(item.new_value)

            new_values.forEach((value, i) => {
                result.push(`${keys[i]}: ${value}`)
            })

            return result
        }

        const showModal = (item: ChangesHistoryItemT) => {
            state.current_item = item
        }

        const closeModal = () => {
            state.current_item = null
        }

        const isEditItem = computed(() => {
            return state.current_item && state.current_item.actionId === 2
        })

        const isSearch = computed(() => {
            return filters_state.typeId && filters_state.clientId
        })

        return {
            ChangesHistoryColumns,
            state,
            data_state,
            getDifferences,
            loadChangesHistory,
            onSubmitFilters,
            onResetFilters,
            closeModal,
            showModal,
            getNewValueArr,
            //
            isEditItem,
            isSearch,
        }
    },
})
